import cn from 'classnames'
import { getMemberAuthStatusActionCreator } from 'actions/memberActions'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { SimpleGuard } from 'interfaces/guards'
import { FC, useMemo } from 'react'
import { Redirect } from 'react-router-dom'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import styles from 'components/features/creditCard/creditCardPage/CreditCardPage/CreditCardPage.styl'
import { ccStateGuard } from 'components/features/creditCard/creditCardStore/guards/ccStateGuard'
import { CreditCardPagePreset } from 'components/features/creditCard/creditCardPage/CreditCardPagePreset/CreditCardPagePreset'
import { RecentTransactions } from 'components/features/creditCard/creditCardPage/RecentTransactions/RecentTransactions'
import { Rewards } from 'components/features/creditCard/creditCardPage/Rewards/Rewards'
import { Coaching } from 'components/features/creditCard/creditCardPage/Coaching/Coaching'
import HighlightsContainer from 'components/features/pfm/Highlights/HighlightsContainer'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { CC_ONBOARDING_STATE_FINISHED, RECENT_TRANSACTIONS_HINT } from 'components/features/creditCard/creditCardStore/constants'
import { CardApplicationPage } from 'components/features/creditCard/creditCardPage/CardApplicationPage/CardApplicationPage'
import ModuleLayout from 'components/atoms/ModuleLayout/ModuleLayout'
import { TwoColumnRowLayout } from 'components/layouts/Layouts'
import { CardSummary } from 'components/features/creditCard/creditCardPage/CardSummary/CardSummary'
import { NextPayment } from 'components/features/creditCard/creditCardPage/NextPayment/NextPayment'
import {
  cardPaymentMethodsSelector,
  cardPaymentsSelector,
  cardUpcomingPaymentsSelector,
  currentCardSelector,
  currentCCLedgerSelector,
  unlockCardRequestSelector,
} from 'components/features/creditCard/creditCardStore/selectors'
import { AddPaymentMethodsPaneButton, AutoPayPaneCTAButton, CardDetailsPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import { useLocalPreferences } from 'components/features/preferences/Preferences'
import { GetCCOnboardingStateAction } from 'components/features/creditCard/creditCardStore/actions'
import { ActivateCardActionButton } from 'components/features/pfm/Button/ActionButtons'
import { find } from 'lodash'

const EMPTY_OBJ = {}

const isAuthenticatedGuard: SimpleGuard = {
  name: `isAuthenticated Guard`,
  checker: (state) => {
    const isAuth = isAuthenticatedSelector(state)
    return isAuth !== null
  },
  loader: (dispatch) => {
    dispatch(getMemberAuthStatusActionCreator())
    dispatch(new GetCCOnboardingStateAction())
  },
}

export const CreditCardPage: FC = () => {
  useSimpleGuard(isAuthenticatedGuard, {}, 'CreditCardPage')
  const isAuth = useStoreSelector(isAuthenticatedSelector)

  if (isAuth === null) {
    return <LoadingOverlay />
  } else if (isAuth) {
    return <CreditCardPageAuth />
  }

  return (
    <Redirect
      to={{
        pathname: '/login',
        search: `?returnUrl=credit-card`,
      }}
    />
  )
}

const CreditCardPageAuth: FC = () => {
  const isLoaded = useSimpleGuard(ccStateGuard, EMPTY_OBJ, 'CreditCardPageAuth')
  const onboarding = useStoreSelector((state) => state.data.creditCard.onboarding)

  if (!isLoaded || !onboarding) {
    return <LoadingOverlay />
  }

  if (onboarding.state === CC_ONBOARDING_STATE_FINISHED) {
    return <CreditCardPageContent />
  }

  return <CardApplicationPage />
}

const CreditCardPageContent: FC = () => {
  const { isLoading } = useStoreSelector(unlockCardRequestSelector)
  const ledger = useStoreSelector(currentCCLedgerSelector)
  const card = useStoreSelector(currentCardSelector)
  const paymentMethods = useStoreSelector(cardPaymentMethodsSelector)
  const pastPayments = useStoreSelector(cardPaymentsSelector)
  const hasPaymentMethods = !!paymentMethods && paymentMethods?.length > 0
  const cardIsAwaitingActivation = card?.status === 'awaiting_activation'
  const upcomingPayments = useStoreSelector(cardUpcomingPaymentsSelector)
  const autoPayment = !!upcomingPayments ? find(upcomingPayments, ['schedule_type', 'autopay']) : undefined
  const transactions = useStoreSelector((state) => state.data.creditCard.transactions)

  useLocalPreferences() // TODO - move up to the whole app

  const isNewCardMember = (ledger?.current_balance === '0.00' && !pastPayments) || cardIsAwaitingActivation

  const welcomeChild = useMemo(() => isNewCardMember && (
    <ModuleLayout key='summary' title='Getting Started' className={ styles.welcomeModule } >
      <h2>{ cardIsAwaitingActivation ? 'Get started with your new card!' : 'Your card is active and ready for use.' }</h2>
      <p>While you wait for your physical card to arrive, you can start using it to shop online immediately.</p>
      { cardIsAwaitingActivation && <p>Activate your card to begin.</p> }
      {
        cardIsAwaitingActivation && !!card?.uid
          ? <ActivateCardActionButton text='Activate Your Card Now' uid={ card.uid } isSubmitting={ isLoading } />
          : (
            <>
              <CardDetailsPaneButton text='See your card number' size={ hasPaymentMethods ? 'medium' : 'small' } backButton={ null } />
              {
                !hasPaymentMethods ? (
                  <AddPaymentMethodsPaneButton text='Add a payment method' size='small' theme='lightBlue' backButton={ null } />
                ) : (
                  !autoPayment && <AutoPayPaneCTAButton theme='lightBlue' />
                )
              }
            </>
          )
      }
    </ModuleLayout>
  ), [card?.uid, cardIsAwaitingActivation, hasPaymentMethods, isLoading, isNewCardMember])

  const isMobile = useStoreSelector(isMobileSelector)
  const summaryChild = useMemo(() => (
    <ModuleLayout key='summary' title='Summary' className={ styles.summaryModule } defaultVariant={ 3 }>
      <CardSummary />
    </ModuleLayout>
  ), [])

  const rewardsChild = useMemo(() => (
    <ModuleLayout key='rewards' title='Rewards' className={ styles.rewardsModule }>
      <Rewards />
    </ModuleLayout>
  ), [])

  const nextPaymentChild = useMemo(() => (
    <ModuleLayout key='payment' title='Next Payment' className={ styles.nextPaymentModule }>
      <NextPayment />
    </ModuleLayout>
  ), [])

  const coachingChild = useMemo(() => (
    <ModuleLayout key='coaching' title='Coaching' className={ styles.coachingModule }>
      <Coaching />
    </ModuleLayout>
  ), [])

  const recentTransactionsChild = useMemo(() => (
    <ModuleLayout
      key='transactions'
      title='Recent Transactions'
      className={ cn(styles.transactionsModule, { [styles.fixedHeight]: !transactions || transactions.allKeys.length < 6 }) }
      padding='none'
      hint={ RECENT_TRANSACTIONS_HINT }
    >
      <RecentTransactions />
    </ModuleLayout>
  ), [transactions])

  const contentChild = useMemo(() => isMobile ? (
    <>
      { summaryChild }
      { welcomeChild || nextPaymentChild }
      { recentTransactionsChild }
      { rewardsChild }
      { coachingChild }
    </>
  ) : (
    <TwoColumnRowLayout
      left={ [summaryChild, rewardsChild, coachingChild] }
      right={ [welcomeChild || nextPaymentChild, recentTransactionsChild] }
    />
  ), [isMobile, summaryChild, welcomeChild, nextPaymentChild, recentTransactionsChild, rewardsChild, coachingChild])

  const footerChild = useMemo(() => (
    <section className={ styles.quickLookSection }>
      <HighlightsContainer placement='creditcard_carousel' />
    </section>
  ), [])

  return (
    <CreditCardPagePreset footerChild={ footerChild }>
      { contentChild }
    </CreditCardPagePreset>
  )
}

